import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * Markdown to HTML, sanitised, in the browser (D260815c).
 *
 * **The order is the whole function.** `marked` produces HTML from whatever was
 * written, including any HTML that was pasted into the note verbatim; DOMPurify
 * is what decides which of it survives. Sanitising first and rendering after
 * would hand a fresh, unexamined document straight to `dangerouslySetInnerHTML`.
 *
 * The Content-Security-Policy is the second line and not the first. `script-src
 * 'self'` already makes a pasted `<script>` inert and blocks an inline
 * `onerror`, so a mistake here is not immediately an exploit — but a note is
 * rendered into the same document as the session, and one defence that has to be
 * right is one defence too few.
 *
 * A visible consequence, worth knowing before it is reported as a bug:
 * `img-src 'self' data:` means a note holding a remote image shows a broken one.
 * That is the policy working. "The browser makes no request that does not go to
 * this instance" is a sentence `specs/01` means literally, and a note is not the
 * place to start making exceptions to it.
 */

/** `breaks: true` — a single newline is a line break, which is how notes are typed. */
const OPTIONS = { async: false, gfm: true, breaks: true } as const;

let purifier: ReturnType<typeof DOMPurify> | null = null;

/**
 * The sanitiser, bound to the window on first use rather than at import.
 *
 * DOMPurify binds itself to `window` the moment it is imported, and a module
 * imported where there is none — a test, in Node — gets an instance whose
 * `sanitize` quietly returns its input unchanged. Binding lazily means the same
 * module works in both, and that the one place this could go silently wrong
 * cannot be reached by an import order.
 */
function sanitiser(): ReturnType<typeof DOMPurify> {
  purifier ??= DOMPurify(window);
  return purifier;
}

export function renderMarkdown(source: string): string {
  return sanitiser().sanitize(marked.parse(source, OPTIONS));
}
